// --- Dependencies -------------------------------------------------------------------------------
// --- Our Modules --------------------------------------------------------------------------------
import useTranslation from "@/hooks/useTranslation";
import type { TyCouponDiscount } from "@/types";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    code: string;
    discount: TyCouponDiscount;
    /** Pounds actually taken off this purchase — a percentage means nothing without it. */
    savedAmount: number;
    /** Renders a remove control; omit wherever the coupon is already committed to. */
    onRemove?: () => void;
    disabled?: boolean;
    /** Tighter variant for summaries that sit inside another card. */
    compact?: boolean;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * The redeemed coupon, stated loudly. This is the one moment the client gets something for free, so
 * the rate is the largest thing on the row and the code sits beside it as proof of what earned it.
 *
 * A percentage is shown as the rate the client was promised (`-26%`) with the pounds underneath,
 * while a fixed coupon is already in pounds and would just repeat itself — so it shows only once.
 */
export default function CouponBadge({ code, discount, savedAmount, onRemove, disabled, compact }: Props) {
    const t = useTranslation();

    const isPercentage = discount.type === "percentage";
    // The numeral carries the minus and stays LTR; the currency word is a separate run so Arabic does
    // not get reordered around it, and it reads as a unit beside the figure rather than part of it.
    const figure = isPercentage ? `${discount.amount}%` : discount.amount.toLocaleString();

    return (
        <div className={`coupon-badge${compact ? " coupon-badge--compact" : ""}`}>
            <div className="coupon-badge-main">
                <div className="coupon-badge-code-block">
                    <span className="coupon-badge-label">{t.subscriptions.couponLabel}</span>
                    <span className="coupon-badge-code" dir="ltr">
                        {code}
                    </span>
                </div>

                <div className="coupon-badge-rate-block">
                    <span className="coupon-badge-rate">
                        <span dir="ltr">−{figure}</span>
                        {!isPercentage && <span className="coupon-badge-rate-unit">{t.subscriptions.egp}</span>}
                    </span>
                    {/* A percentage needs its pound value spelled out, but only once — the footer says
                        it whenever there is one, so the sub-line is for the footer-less variant. */}
                    {isPercentage && !onRemove && (
                        <span className="coupon-badge-saved">
                            {savedAmount.toLocaleString()} {t.subscriptions.egp}
                        </span>
                    )}
                </div>
            </div>

            {onRemove && (
                <div className="coupon-badge-footer">
                    <span className="coupon-badge-note">
                        {t.subscriptions.couponSavedLabel} {savedAmount.toLocaleString()} {t.subscriptions.egp}
                    </span>
                    <button type="button" className="coupon-badge-remove" onClick={onRemove} disabled={disabled}>
                        {t.subscriptions.couponRemove}
                    </button>
                </div>
            )}
        </div>
    );
}
